'use client';

/**
 * **The treatment's `Yes` / `No` segmented control, on its own** (extracted by
 * LF-6314 from `attribute-boolean.tsx`, which LF-6350 / [ATTR-8] built).
 *
 * [ATTR-8] argued this control into the product and then wired it to a
 * `AttributeSpec` — the grid's field descriptor — so the only way to render one
 * was to have a spec, a focus index and the grid's commit path. LF-6314 needs the
 * same control for a boolean that is **not** a grid row: the inventory drawer's
 * "Confirmed against a source", which belongs to a figure rather than to the
 * record, and whose `Yes` reveals two more controls.
 *
 * So the control moves here and `AttributeBooleanSegmented` keeps the grid
 * plumbing — the `Tab` chain, the focus-on-arrival, the commit — and delegates
 * the rendering. Composing the track, the sliding pill and the two segments a
 * second time in the inventory folder is the alternative, and it is the exact
 * shape the root `CLAUDE.md` calls a second drifting definition of one fact: the
 * pill's `w-[calc(50%-2px)]` and its `translate-x-full` are a *geometry
 * agreement between two elements*, so a copy that keeps one token and loses the
 * other is broken in a way no test would catch.
 *
 * **Presentational and spec-free on purpose.** It owns no state and no keyboard
 * behaviour beyond what `ToggleGroup` gives it, because its two callers need
 * different keyboard behaviour: the grid row is a stop in a `Tab` chain, and the
 * confirmation control is an ordinary focusable control.
 *
 * (It sits in `components/attributes/` beside the tokens it is drawn from —
 * LF-6388 / [ATTR-10] moved both out of `components/leads/` with the rest of the
 * shared form system.)
 */

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_SEGMENTED_PILL,
  ATTRIBUTE_SEGMENTED_SEGMENT,
  ATTRIBUTE_SEGMENTED_TRACK
} from './attribute-tokens';

export function AttributeSegmentedBoolean({
  value,
  onValueChange,
  onUnset,
  ariaLabel,
  yesLabel = 'Yes',
  noLabel = 'No',
  disabled = false,
  className,
  segmentClassName
}: {
  /**
   * `null` is *neither segment lit*, and it is only reachable with
   * {@link allowUnset} — see it for why that is a state rather than a gap.
   */
  value: boolean | null;
  onValueChange: (next: boolean) => void;
  ariaLabel: string;
  /** Overridden only where `Yes` / `No` would not be the words a lawyer reads. */
  yesLabel?: string;
  noLabel?: string;
  /**
   * **Clicking the lit segment un-answers the row** — supply this and the
   * deselect becomes reachable; omit it and it stays swallowed.
   *
   * Deriving the behaviour from the handler rather than from a separate flag is
   * deliberate, and not only for tidiness: a `boolean` caller's own
   * `onValueChange` takes a `boolean`, so widening *that* to `boolean | null`
   * would have broken all three existing call sites on contravariance alone —
   * the type system saying the same thing this API says, that un-answering is a
   * different act with a different result, not a third value of the same one.
   *
   * Supplied by a field whose vocabulary has a real member meaning *nobody has
   * answered yet* — the enquiry's `Not asked yet` / `Not recorded`. The caller
   * maps it onto its own stored member; this component knows nothing about it.
   */
  onUnset?: () => void;
  disabled?: boolean;
  className?: string;
  /**
   * Classes for the LIT segment, the only part of this control that stands
   * for the value (VALE-1208). The unlit answer is not the value and never
   * takes them: a plain text class here would otherwise colour both words, since
   * the segment's own `text-muted-foreground` gives way to it. A segment also
   * sets `data-[state=on]:text-foreground`, so a caller colouring the answer
   * restates its colour on that state; a class on the track never reaches it.
   */
  segmentClassName?: string;
}) {
  return (
    <ToggleGroup
      type="single"
      size="sm"
      value={value === null ? '' : value ? 'yes' : 'no'}
      /*
        Radix reports an empty string when the lit segment is clicked again.
        Whether that is a state depends on the vocabulary, so it is the caller's
        call: swallowed for a boolean (the original contract), forwarded as
        `null` for a field that can genuinely be un-answered.
      */
      onValueChange={(next) => {
        if (!next) {
          onUnset?.();
          return;
        }
        onValueChange(next === 'yes');
      }}
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn(ATTRIBUTE_SEGMENTED_TRACK, className)}
    >
      {/*
        Decoration drawn *behind* a real control: `ToggleGroup` still owns the
        interaction, the keyboard and the accessibility tree, and the segments are
        merely transparent. This is not a hand-rolled replacement for one.
      */}
      {/* No pill at all while unset — the two segments read as offered rather
          than as one of them chosen, which is the whole point of the state. */}
      {value !== null && (
        <span
          aria-hidden
          className={cn(
            ATTRIBUTE_SEGMENTED_PILL,
            value ? 'translate-x-0' : 'translate-x-full'
          )}
        />
      )}
      <ToggleGroupItem
        value="yes"
        className={cn(
          ATTRIBUTE_SEGMENTED_SEGMENT,
          value === true && segmentClassName
        )}
      >
        {yesLabel}
      </ToggleGroupItem>
      <ToggleGroupItem
        value="no"
        className={cn(
          ATTRIBUTE_SEGMENTED_SEGMENT,
          value === false && segmentClassName
        )}
      >
        {noLabel}
      </ToggleGroupItem>
    </ToggleGroup>
  );
}
